import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ListChecks, ChevronRight } from 'lucide-react';
import $ from 'jquery';
import axios from 'axios';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import 'datatables.net-responsive';

const CowSelectionList = ({ planState, setPlanState, selectedFarm, setDialog, onNext, nextLabel }) => {
    const tableRef = useRef(null);
    const dtApi = useRef(null);
    const [availableCows, setAvailableCows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const selectedCowIdsRef = useRef(new Set());
    const availableCowsRef = useRef([]); // availableCows의 최신 값을 저장할 ref
    const [cowType, setCowType] = useState('A'); // 'A' for 전체, 'P' for P
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const fetchCows = async () => {
            if (!selectedFarm?.id) return;
            setLoading(true);
            setError(null);
            try {
                const formData = new FormData();
                formData.append('member_id', selectedFarm.id);
                formData.append('gubun', cowType);
                const response = await axios.post('/api/cow', formData);
                setAvailableCows(response.data?.list || []);
            } catch (err) {
                console.error("농장 암소 목록 조회 실패:", err);
                setError("암소 목록을 불러오는 중 오류가 발생했습니다.");
                setAvailableCows([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCows();
    }, [selectedFarm.id, cowType]);

    // availableCows 상태가 변경될 때마다 ref를 업데이트합니다.
    useEffect(() => {
        availableCowsRef.current = availableCows;
    }, [availableCows]);

    const currentCowIds = useMemo(() => new Set(planState.selectedCows.map(c => c.cow_regno)), [planState.selectedCows]);

    useEffect(() => {
        selectedCowIdsRef.current = currentCowIds;
        if (dtApi.current) {
            // dtApi.current.draw(false); // Removed to prevent scroll jump

            // Manually update the visual state of the current page's rows
            const api = dtApi.current;
            api.rows({ page: 'current' }).nodes().to$().each(function () {
                const row = $(this);
                const rowData = api.row(this).data();
                const checkbox = row.find('.datatable-checkbox');

                if (rowData && selectedCowIdsRef.current.has(rowData.cow_regno)) {
                    row.css('background-color', '#eff6ff');
                    checkbox.prop('checked', true);
                } else {
                    row.css('background-color', '');
                    checkbox.prop('checked', false);
                }
            });

            // Update "Select All" checkbox state
            const allRows = api.rows({ search: 'applied' }).data().toArray();
            const allSelected = allRows.length > 0 && allRows.every(row => selectedCowIdsRef.current.has(row.cow_regno));
            $('#select-all-cows').prop('checked', allSelected);
        }
    }, [currentCowIds]);

    const handleToggleCowSelection = (cow) => {
        const isSelected = selectedCowIdsRef.current.has(cow.cow_regno);
        setPlanState(prev => {
            const newSelectedCows = isSelected
                ? prev.selectedCows.filter(c => c.cow_regno !== cow.cow_regno)
                : [...prev.selectedCows, cow];
            return { ...prev, selectedCows: newSelectedCows };
        });
    };

    const columns = [
        { title: '<input type="checkbox" id="select-all-cows" />', data: null, defaultContent: '', orderable: false, className: 'dt-body-center' },
        { title: '관리번호', data: 'mgmt_no' },
        { title: '단축명호', data: 'cow_sname', className: 'dt-head-left-icon' },
        { title: '생년월일', data: 'cow_birthday', className: 'dt-head-left-icon' },
        { title: '명호', data: 'cow_nname' },
        { title: '부_정액이름(한글)', data: 'nm_fsemen' },
        { title: '등록번호', data: 'cow_regno' },
    ];

    useEffect(() => {
        const options = {
            columns: columns,
            responsive: { details: { type: 'inline' } },
            paging: true,
            lengthChange: false,
            pageLength: 20,
            searching: true, // API 검색 활성화
            dom: 'lrtip', // 기본 검색창 숨김
            ordering: true, // 정렬 활성화
            language: {
                emptyTable: "농장에 등록된 암소가 없습니다.",
                paginate: { first: "처음", last: "마지막", next: "다음", previous: "이전" },
                info: "_TOTAL_건 중 _START_에서 _END_까지 표시",
                infoEmpty: "0건",
            },
            columnDefs: [
                {
                    targets: 0,
                    width: '30px',
                    responsivePriority: 1,
                    orderable: false,
                    className: 'dt-body-center',
                    render: (data, type, row) => `<input type="checkbox" class="datatable-checkbox" data-cow-id="${row.cow_regno}" onclick="event.stopPropagation()" />`
                },
                { responsivePriority: 2, targets: 1, orderable: false }, // 관리번호 정렬 비활성화
                { targets: [4, 5, 6], orderable: false }, // 나머지 컬럼(명호, 부_정액이름, 등록번호) 정렬 비활성화
                // 단축명호(2), 생년월일(3)은 기본적으로 정렬 활성화됨  

            ],
            drawCallback: function () {
                const api = this.api();
                api.responsive.recalc();

                api.rows({ page: 'current' }).nodes().to$().each(function () {
                    const row = $(this);
                    const rowData = api.row(this).data();
                    const checkbox = row.find('.datatable-checkbox');

                    if (rowData && selectedCowIdsRef.current.has(rowData.cow_regno)) {
                        row.css('background-color', '#eff6ff');
                        checkbox.prop('checked', true);
                    } else {
                        row.css('background-color', '');
                        checkbox.prop('checked', false);
                    }
                });

                // 헤더 체크박스 상태 업데이트
                const allRows = api.rows({ search: 'applied' }).data().toArray();
                const allSelected = allRows.length > 0 && allRows.every(row => selectedCowIdsRef.current.has(row.cow_regno));
                $('#select-all-cows').prop('checked', allSelected);
            }
        };

        // DataTables 초기화 전에 테이블 요소를 비웁니다.
        $(tableRef.current).empty();
        dtApi.current = $(tableRef.current).DataTable(options);

        $(tableRef.current).on('change', '.datatable-checkbox', function () {
            const cowId = $(this).data('cow-id');
            const cowToToggle = availableCowsRef.current.find(c => c.cow_regno === cowId);
            if (cowToToggle) {
                handleToggleCowSelection(cowToToggle);
            }
        });

        // 체크박스 클릭 시 이벤트 전파 방지 (행 확장/축소 방지)
        $(tableRef.current).on('click', '.datatable-checkbox', function (e) {
            e.stopPropagation();
        });

        // 전체 선택 체크박스 이벤트 핸들러
        $(tableRef.current).on('change', '#select-all-cows', function () {
            const isChecked = $(this).prop('checked');
            const rows = dtApi.current.rows({ search: 'applied' }).data().toArray();

            setPlanState(prev => {
                const currentSelectedIds = new Set(prev.selectedCows.map(c => c.cow_regno));
                let newSelectedCows = [...prev.selectedCows];

                if (isChecked) {
                    // 선택되지 않은 행 추가
                    rows.forEach(row => {
                        if (!currentSelectedIds.has(row.cow_regno)) {
                            newSelectedCows.push(row);
                        }
                    });
                } else {
                    // 현재 필터링된 행들 중 선택된 것들을 제거
                    const rowIdsToRemove = new Set(rows.map(r => r.cow_regno));
                    newSelectedCows = newSelectedCows.filter(c => !rowIdsToRemove.has(c.cow_regno));
                }
                return { ...prev, selectedCows: newSelectedCows };
            });
        });

        return () => {
            if (dtApi.current) {
                $(tableRef.current).off('change');
                dtApi.current.destroy();
                dtApi.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (dtApi.current) {
            dtApi.current.clear().rows.add(availableCows).draw();
        }
    }, [availableCows]);

    return (
        <div className="card" style={{ position: 'relative', minHeight: '300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', fontWeight: '600', margin: 0 }}>
                    <ListChecks size={18} style={{ marginRight: '0.5rem' }} />
                    농장 보유 암소 목록 ({selectedFarm.name})
                </h4>
                {onNext && (
                    <button onClick={onNext} className="action-button" style={{ padding: '0.45rem 1rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        {nextLabel} <ChevronRight size={16} />
                    </button>
                )}
            </div>
            <div>
                <div className="search-bar-container" style={{ marginBottom: '1rem' }}>
                    <input
                        type="search"
                        placeholder="단축명호 검색"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            if (dtApi.current) {
                                dtApi.current.column(1).search(e.target.value).draw();
                            }
                        }}
                        className="form-input"
                    />
                </div>
                <div className="button-group-row">
                    <button
                        className={`toggle-button ${cowType === 'A' ? 'active' : ''}`}
                        onClick={() => setCowType('A')}
                    >
                        전체
                    </button>
                    <button
                        className={`toggle-button ${cowType === 'P' ? 'active' : ''}`}
                        onClick={() => setCowType('P')}
                    >
                        가임암소
                    </button>
                    <button
                        className={`toggle-button ${cowType === 'S' ? 'active' : ''}`}
                        onClick={() => setCowType('S')}
                    >
                        심사우
                    </button>
                </div>
            </div>
            {(loading || error) && (
                <div className="loading-overlay">
                    <div className="loading-message" style={{ color: error ? '#fecaca' : 'white' }}>
                        {loading ? '암소 목록을 불러오는 중...' : error}
                    </div>
                </div>
            )}
            <div className="table-container">
                <table ref={tableRef} className="display nowrap" style={{ width: '100%' }}></table>
            </div>
        </div>
    );
};

export default CowSelectionList;
